import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth, useLanguage, api } from '../context/AppContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import LegalShieldModal from '../components/LegalShieldModal';
import CoachYumixAvatar from '../components/CoachYumixAvatar';
import { v4 as uuidv4 } from 'uuid';
import { 
  Send, 
  UserCircle, 
  User, 
  Loader2, 
  Plus,
  Sparkles,
  MessageSquare,
  Trash2,
  ChevronRight,
  Lightbulb,
  Zap,
  Clock,
  ArrowLeft,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  Stethoscope,
  Navigation,
  Weight,
  UtensilsCrossed,
  Droplets,
  CheckCircle2,
  ShoppingCart,
  ChefHat,
  Dumbbell,
  Heart,
  Brain,
  FlameKindling,
  Download
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import ClinicalModeIndicator from '../components/ClinicalModeIndicator';
import CoachMessageContent from '../components/CoachMessageContent';
import PremiumIcon from '../components/PremiumIcon';
import ErrorLogger from '../utils/errorLogger';

const CoachAI = () => {
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const sessionIdRef = useRef(null);
  
  // Helper: Always persist session_id to localStorage + ref + state
  const updateSessionId = (newId) => {
    sessionIdRef.current = newId;
    setSessionId(newId);
    if (newId) {
      localStorage.setItem('coach_session_id', newId);
    } else {
      localStorage.removeItem('coach_session_id');
    }
  };
  const [conversations, setConversations] = useState([]);
  const [showConversations, setShowConversations] = useState(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [pillarOrder, setPillarOrder] = useState([]);
  // Iter. 406.fn — Coach Sportif sub-menu: when active, the 5-pillar grid
  // is replaced by 4 educational chips (techniques / supplements / recovery /
  // periodization) so the user goes straight to the topic they care about
  // instead of typing a generic message.
  const [coachSubMenu, setCoachSubMenu] = useState(false);
  const [quickTips, setQuickTips] = useState([]);
  const [loadError, setLoadError] = useState(null);
  // 🛡️ Zéro Relais — Legal shield modal state
  const [shieldState, setShieldState] = useState({
    open: false,
    resources: null,
    topics: [],
  });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // ========== V2 VOICE MODULE — MACHINE À ÉTATS STRICTE ==========
  // States: IDLE → RECORDING → PROCESSING → PLAYING → IDLE
  const [voiceState, setVoiceState] = useState('IDLE'); // Single source of truth
  const [voiceMode, setVoiceMode] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [clinicalMode, setClinicalMode] = useState(null);
  const [lastTranscription, setLastTranscription] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioPlayerRef = useRef(null);
  // Generation counter for audio playback — any stale clip whose generation no longer matches is ignored.
  // Incremented on every killAllAudio() / new play to guarantee strict serial playback.
  const audioGenerationRef = useRef(0);
  const streamRef = useRef(null);
  const voiceLockRef = useRef(false); // Hard lock — synchronous, no race conditions

  // Derived states from voiceState (no separate booleans)
  const isRecording = voiceState === 'RECORDING';
  const processingVoice = voiceState === 'PROCESSING';
  const isAudioPlaying = voiceState === 'PLAYING';

  // Scroll to bottom of messages — ONLY if user is already near the bottom
  const scrollContainerRef = useRef(null);
  const userScrolledUpRef = useRef(false);

  const scrollToBottom = useCallback((force = false) => {
    setTimeout(() => {
      const container = scrollContainerRef.current;
      if (!container) return;
      // Find the actual scrollable viewport inside ScrollArea
      const viewport = container.querySelector('[data-radix-scroll-area-viewport]');
      if (!viewport) return;
      
      const { scrollTop, scrollHeight, clientHeight } = viewport;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 120;
      
      if (force || isNearBottom || !userScrolledUpRef.current) {
        viewport.scrollTop = scrollHeight;
        userScrolledUpRef.current = false;
      }
    }, 50);
  }, []);

  useEffect(() => {
    // Only auto-scroll for new messages, not re-renders
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages.length, scrollToBottom]);

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      setInitialLoading(true);
      setLoadError(null);
      try {
        await Promise.all([
          loadSuggestedQuestions(),
          loadQuickTips(),
          loadConversations(),
          loadPillarOrder()
        ]);
        // Fetch clinical mode status
        const clinicalRes = await api.get('/users/clinical-mode');
        setClinicalMode(clinicalRes.data.clinical_mode);
      } catch (error) {
        console.error('Error loading initial data:', error);
        setLoadError('Erreur de chargement. Veuillez réessayer.');
      } finally {
        setInitialLoading(false);
      }
    };
    loadInitialData();
  }, []);

  // First-time "Onboarding Briefing" — inject a pre-programmed welcome message
  // with 3 deep-link buttons so the user sees the AI in action on day 1.
  // Triggered once per user (localStorage flag). Auto-opens the chat view.
  useEffect(() => {
    if (!user?.user_id) return;
    if (initialLoading) return;
    const flagKey = `yumix_ai_briefing_seen_${user.user_id}`;
    try {
      if (localStorage.getItem(flagKey)) return;
    } catch { return; }

    const firstName = (user.name || '').split(' ')[0] || '';
    const fr = language === 'fr';
    const briefingContent = fr
      ? `Salut ${firstName ? firstName + ' ' : ''}! Je suis ton Assistant IA YUMIX.

Mon seul objectif : te faire gagner du temps. Avec moi, tu n'as plus besoin de chercher dans les menus. Si tu veux quelque chose, ecris-le-moi.

Voici mon arsenal tactique :
📸 Friction Zero : Prends ton assiette en photo, je calcule tes macros.
⚖️ Ton profil : Dis-moi "Je pese 185 lbs ce matin", et j'enregistre tout.
🆘 Urgence : Dis-moi "J'ai faim de sucre", et je te sors un plan d'action.

Teste-moi tout de suite ! Clique sur un des boutons ci-dessous pour voir comment je travaille :

[[GO:/evolution?tab=weight|⚖️ Mettre a jour mon poids]]
[[GO:/meals|📸 Ajouter un repas]]
[[GO:/guide|📖 Lire le Guide YUMIX]]`
      : `Hi ${firstName ? firstName + ' ' : ''}! I'm your YUMIX AI Assistant.

My only mission: save you time. With me, no more hunting through menus. If you want something, just write it to me.

Here's my tactical arsenal:
📸 Zero friction: Photograph your plate, I compute your macros.
⚖️ Your profile: Tell me "I weigh 185 lbs this morning" and I log it all.
🆘 Emergency: Tell me "I'm craving sugar" and I give you a battle plan.

Test me right now! Tap one of the buttons below to see how I work:

[[GO:/evolution?tab=weight|⚖️ Update my weight]]
[[GO:/meals|📸 Log a meal]]
[[GO:/guide|📖 Read the YUMIX Guide]]`;

    // Seed a virtual session so the chat UI renders
    const briefingSessionId = `briefing_${user.user_id}`;
    updateSessionId(briefingSessionId);
    setMessages([{
      role: 'assistant',
      content: briefingContent,
      timestamp: new Date().toISOString(),
      msg_id: 'briefing-msg',
    }]);

    try { localStorage.setItem(flagKey, new Date().toISOString()); } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id, initialLoading]);


  const loadSuggestedQuestions = async () => {
    const response = await api.get('/coach-ai/suggested-questions');
    setSuggestedQuestions(response.data.questions || []);
  };

  const loadQuickTips = async () => {
    const response = await api.get('/coach-ai/quick-tips');
    setQuickTips(response.data.tips || []);
  };

  const loadConversations = async () => {
    const response = await api.get('/coach-ai/conversations');
    const convs = response.data.conversations || [];
    setConversations(convs);
    
    // DO NOT auto-load last conversation — show the Command Center by default
    // Users can tap a conversation from the sidebar to load it
  };

  const loadPillarOrder = async () => {
    try {
      const res = await api.get('/coach-ai/pillar-order');
      setPillarOrder(res.data.order || []);
    } catch { /* first visit — no data yet */ }
  };

  const trackPillarClick = (pillarId) => {
    api.post('/coach-ai/pillar-click', { pillar_id: pillarId }).catch(() => {});
  };

  // Start new conversation
  const startNewConversation = async () => {
    setLoading(true);
    try {
      const response = await api.post('/coach-ai/new-session');
      const newId = response.data.session_id;
      updateSessionId(newId);
      setMessages([{
        role: 'assistant',
        content: response.data.welcome_message,
        timestamp: response.data.created_at
      }]);
      setShowConversations(false);
      setTimeout(() => scrollToBottom(true), 200);
      loadConversations();
    } catch (error) {
      toast.error('Erreur lors de la création de la conversation');
    } finally {
      setLoading(false);
    }
  };

  // Load existing conversation
  const loadConversation = async (convSessionId) => {
    setLoading(true);
    try {
      const response = await api.get(`/coach-ai/conversation/${convSessionId}`);
      updateSessionId(convSessionId);
      setMessages(response.data.messages);
      setShowConversations(false);
      setTimeout(() => scrollToBottom(true), 200);
    } catch (error) {
      toast.error('Erreur lors du chargement de la conversation');
    } finally {
      setLoading(false);
    }
  };

  // Delete conversation
  const deleteConversation = async (convSessionId, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/coach-ai/conversation/${convSessionId}`);
      setConversations(prev => prev.filter(c => c.session_id !== convSessionId));
      if (sessionId === convSessionId) {
        updateSessionId(null);
        setMessages([]);
      }
      toast.success('Conversation supprimée');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Send text message — STREAMING SSE (Feb 2026) for <2s time-to-first-byte.
  const sendMessage = async (text = message) => {
    if (!text.trim() || sending) return;

    const trimmed = text.trim();
    const msgId = uuidv4();
    const assistantMsgId = uuidv4();

    setMessages(prev => [
      ...prev,
      { role: 'user', content: trimmed, timestamp: new Date().toISOString(), msg_id: msgId },
      { role: 'assistant', content: '', timestamp: new Date().toISOString(), msg_id: assistantMsgId, streaming: true },
    ]);
    setMessage('');
    setSending(true);

    const appendDelta = (delta) => {
      setMessages(prev => prev.map(m =>
        m.msg_id === assistantMsgId ? { ...m, content: (m.content || '') + delta } : m
      ));
    };

    const finishAssistant = (finalText, timestamp) => {
      setMessages(prev => prev.map(m =>
        m.msg_id === assistantMsgId
          ? { ...m, content: finalText ?? m.content, timestamp: timestamp || m.timestamp, streaming: false }
          : m
      ));
    };

    const token = localStorage.getItem('session_token');
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/coach-ai/chat-stream`;

    // Hoisted so the catch block can include partial text in the error log
    // (was previously declared inside `try` → ReferenceError on stream failure).
    let fullText = '';

    try {
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': token || '',
          'Accept': 'text/event-stream',
        },
        body: JSON.stringify({
          message: trimmed,
          session_id: sessionIdRef.current,
          msg_id: msgId,
        }),
      });

      if (!resp.ok || !resp.body) {
        throw new Error(`HTTP ${resp.status}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let doneTimestamp = null;

      // Parse SSE frames (each frame = lines separated by \n, frames separated by \n\n)
      // Each data: line carries a JSON payload.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let frameEnd;
        while ((frameEnd = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, frameEnd);
          buffer = buffer.slice(frameEnd + 2);
          const dataLine = frame.split('\n').find(l => l.startsWith('data:'));
          if (!dataLine) continue;
          const jsonStr = dataLine.slice(5).trim();
          if (!jsonStr) continue;
          let payload;
          try { payload = JSON.parse(jsonStr); } catch { continue; }

          if (payload.type === 'session') {
            if (!sessionIdRef.current && payload.session_id) {
              updateSessionId(payload.session_id);
            }
          } else if (payload.type === 'safety' && payload.resources) {
            // 🛡️ Zéro Relais — trigger legal shield modal
            setShieldState({ open: true, resources: payload.resources, topics: payload.topics || [] });
          } else if (payload.type === 'chunk' && payload.delta) {
            appendDelta(payload.delta);
            fullText += payload.delta;
          } else if (payload.type === 'done') {
            doneTimestamp = payload.timestamp;
            if (payload.full_text) fullText = payload.full_text;
          } else if (payload.type === 'error') {
            throw new Error(payload.error || 'Stream error');
          }
        }
      }

      finishAssistant(fullText || '', doneTimestamp);
      loadConversations();
    } catch (error) {
      console.error('[CoachAI] stream error:', error);
      // Iter. 338 Mai 2026 — silent 1-retry for transient network drops
      // (Tetou 5G coupures). We only surface the failure to the user (and to
      // Bug Radar) if the SECOND attempt also fails. If we already streamed
      // partial text successfully, we do NOT retry — that would duplicate
      // tokens in the assistant bubble.
      const isNetworkBlip = (
        !fullText &&
        (
          error?.name === 'TypeError' ||
          /network error|networkerror|failed to fetch|load failed|the network connection was lost/i.test(error?.message || '')
        )
      );
      if (isNetworkBlip && !sendMessage._retried) {
        sendMessage._retried = true;
        try {
          // 1.2 s backoff to give the cell tower a chance to re-handshake
          await new Promise((r) => setTimeout(r, 1200));
          // Reset assistant bubble for a clean retry
          setMessages(prev => prev.map(m =>
            m.msg_id === assistantMsgId ? { ...m, content: '' } : m
          ));
          const resp2 = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Session-Token': token || '',
              'Accept': 'text/event-stream',
            },
            body: JSON.stringify({
              message: trimmed,
              session_id: sessionIdRef.current,
              msg_id: msgId,
            }),
          });
          if (!resp2.ok || !resp2.body) throw new Error(`HTTP ${resp2.status}`);
          const reader2 = resp2.body.getReader();
          const decoder2 = new TextDecoder('utf-8');
          let buffer2 = '';
          let doneTs2 = null;
          while (true) {
            const { done, value } = await reader2.read();
            if (done) break;
            buffer2 += decoder2.decode(value, { stream: true });
            const frames = buffer2.split('\n\n');
            buffer2 = frames.pop() || '';
            for (const frame of frames) {
              const dataLine = frame.split('\n').find((l) => l.startsWith('data:'));
              if (!dataLine) continue;
              try {
                const payload = JSON.parse(dataLine.slice(5).trim());
                if (payload.delta) {
                  fullText += payload.delta;
                  appendDelta(payload.delta);
                } else if (payload.timestamp) {
                  doneTs2 = payload.timestamp;
                }
              } catch { /* ignore malformed frame */ }
            }
          }
          finishAssistant(fullText || '', doneTs2);
          loadConversations();
          sendMessage._retried = false;
          return; // success on retry — DO NOT log to Bug Radar
        } catch (retryErr) {
          // Retry also failed — fall through to the real error path below
          console.warn('[CoachAI] stream retry also failed:', retryErr);
          error = retryErr;
        }
      }
      sendMessage._retried = false;
      toast.error(error.message || 'Erreur de communication');
      // Remove the empty assistant bubble + user message on failure
      setMessages(prev => prev.filter(m => m.msg_id !== assistantMsgId && m.msg_id !== msgId));
      ErrorLogger.log(error, 'Coach_AI_Text_Stream', {
        status: error?.response?.status,
        has_partial: !!fullText,
      });
    } finally {
      setSending(false);
      scrollToBottom(true);
    }
  };

  // Handle enter key
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ==================== VOICE FUNCTIONS ====================
  
  // Voice mode — direct coaching, no onboarding
  const enableVoiceMode = () => {
    setVoiceMode(true);
  };

  // Auto-trigger voice mode from GlobalFAB shortcut (/coach-ai?auto=voice)
  // Opens voice mode + starts recording immediately (single-tap experience)
  const [autoVoiceArmed, setAutoVoiceArmed] = useState(false);
  useEffect(() => {
    const auto = new URLSearchParams(location.search).get('auto');
    if (auto === 'voice' && !autoVoiceArmed) {
      setAutoVoiceArmed(true);
      setVoiceMode(true);
      // Clean the URL so a refresh does not re-arm
      const next = new URLSearchParams(location.search);
      next.delete('auto');
      navigate({ pathname: location.pathname, search: next.toString() }, { replace: true });
      // Start recording after the mic permissions UI is ready
      setTimeout(() => {
        if (typeof startRecording === 'function') {
          startRecording().catch(() => { /* user may have denied mic */ });
        }
      }, 700);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  
  // ==================== V3: AUDIO TRAFFIC CONTROLLER ====================
  // Single player ref + generation counter acts as a strict serial queue.
  // Any attempt to play a new TTS clip KILLS the currently-playing one first,
  // and any stale audio callback whose generation no longer matches is ignored.
  const killAllAudio = () => {
    // Bump generation so any pending onended from the old clip becomes a no-op.
    audioGenerationRef.current = (audioGenerationRef.current || 0) + 1;
    if (audioPlayerRef.current) {
      try { audioPlayerRef.current.pause(); } catch {}
      try { audioPlayerRef.current.src = ''; } catch {}
      try { audioPlayerRef.current.load(); } catch {}
      audioPlayerRef.current = null;
    }
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      try { window.speechSynthesis.cancel(); } catch {}
    }
  };

  // ========== V2: HARD RESET — Vidange totale ==========
  const hardReset = () => {
    killAllAudio();
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch(e) {}
    }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    audioChunksRef.current = [];
    mediaRecorderRef.current = null;
    setLastTranscription(null);
    voiceLockRef.current = false;
    setVoiceState('IDLE');
  };

  // ========== V2: START RECORDING — DIRECT TO COACHING ==========
  const startRecording = async () => {
    if (voiceLockRef.current || voiceState !== 'IDLE') {
      console.warn(`[V2] Blocked: state=${voiceState} lock=${voiceLockRef.current}`);
      return;
    }
    // 🔴 KILL SWITCH: any ongoing TTS playback is aborted the moment the user
    // presses record. No overlapping voices, ever.
    killAllAudio();
    voiceLockRef.current = true;
    try {
      hardReset();
      setVoiceState('RECORDING');
      voiceLockRef.current = false;
      
      // ⚡ LATENCY OPTIMIZATION: mono + 16kHz + noise suppression for Whisper-friendly audio.
      // Mono + low sample rate reduces payload 3-5x without losing speech intelligibility.
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            channelCount: 1,
            sampleRate: 16000
          }
        });
      } catch (micErr) {
        hardReset();
        console.warn('[CoachAI] Microphone access failed:', micErr);
        if (micErr.name === 'NotAllowedError') {
          toast.error(language === 'fr' ? '🔒 Permission micro refusée' : '🔒 Microphone permission denied');
        } else if (micErr.name === 'NotFoundError') {
          toast.error(language === 'fr' ? '🎤 Aucun microphone détecté' : '🎤 No microphone found');
        } else {
          toast.error(language === 'fr' ? '❌ Vérifiez vos écouteurs et microphone' : '❌ Please check your audio device');
        }
        return;
      }
      streamRef.current = stream;
      // Prefer Opus codec (smallest & widely supported). Fallback to generic WebM or MP4.
      let mimeType = '';
      const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
      for (const c of candidates) {
        if (MediaRecorder.isTypeSupported(c)) { mimeType = c; break; }
      }

      // 24 kbps Opus mono = ~180 KB for a 60s voice message (vs ~1 MB at default bitrate).
      // This cuts upload time by 70-80% on LTE. Whisper accuracy is unaffected.
      const recorderOptions = mimeType
        ? { mimeType, audioBitsPerSecond: 24000 }
        : { audioBitsPerSecond: 24000 };
      let recorder;
      try {
        recorder = new MediaRecorder(stream, recorderOptions);
      } catch {
        // Fallback if browser rejects the bitrate option
        recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      }
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];
      
      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: mimeType || 'audio/webm' });
        if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
        audioChunksRef.current = [];
        await processVoiceMessage(blob);
      };
      recorder.onerror = () => { hardReset(); toast.error('Erreur d\'enregistrement'); };
      recorder.start();
      toast.info(language === 'fr' ? '🎤 Parlez maintenant...' : '🎤 Speak now...');
    } catch (error) {
      hardReset();
      if (error.name === 'NotAllowedError') toast.error(language === 'fr' ? '🔒 Permission micro refusée' : '🔒 Mic denied');
      else toast.error(language === 'fr' ? 'Impossible d\'accéder au micro' : 'Cannot access mic');
    }
  };
  
  // ========== V2: STOP RECORDING ==========
  const stopRecording = () => {
    if (mediaRecorderRef.current && voiceState === 'RECORDING') {
      try { if (mediaRecorderRef.current.state !== 'inactive') mediaRecorderRef.current.stop(); } catch(e) {}
      setVoiceState('PROCESSING');
    }
  };
  
  useEffect(() => { return () => hardReset(); }, []);
  
  // ========== V3: PROCESS VOICE — Split pipeline for instant feedback ==========
  // OLD: single /voice/chat call blocked 10-25s before showing ANYTHING.
  // NEW: /voice/transcribe (~1-2s) → bulle user visible → /coach-ai/chat-stream (SSE) → tokens stream in.
  // If audio is on, TTS is requested in parallel AFTER the stream ends.
  const processVoiceMessage = async (audioBlob) => {
    if (voiceLockRef.current) return;
    voiceLockRef.current = true;
    setVoiceState('PROCESSING');
    setLastTranscription(null);

    const userMsgId = uuidv4();
    const assistantMsgId = uuidv4();

    try {
      // ─── Step 1: Transcribe audio (fast, ~1-2s) ───
      const formData = new FormData();
      formData.append('audio', audioBlob, 'voice.webm');

      const transcribeResp = await api.post('/voice/transcribe', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const transcription = (transcribeResp.data?.text || '').trim();
      if (!transcription) {
        toast.warning(language === 'fr' ? 'Rien détecté, réessaie' : 'Nothing detected');
        hardReset();
        return;
      }
      setLastTranscription(transcription);

      // ─── Echo guard (V3) — ONLY if audio playback is active.
      // If audio is OFF there is zero risk of the mic capturing our own TTS, so
      // skip the check entirely. When audio is ON, compare by SIGNIFICANT WORDS
      // (>3 chars) to avoid false positives from common letter overlap that
      // plagued the V2 implementation.
      if (audioEnabled) {
        const lastAiMsg = [...messages].reverse().find(m => m.role === 'assistant' && m.content);
        if (lastAiMsg?.content && transcription.length > 15) {
          const sigWords = (s) => new Set(
            s.toLowerCase()
              .replace(/[^\p{L}\p{N}\s]/gu, ' ')
              .split(/\s+/)
              .filter(w => w.length > 3)
          );
          const u = sigWords(transcription);
          const a = sigWords(lastAiMsg.content);
          if (u.size >= 5) {
            let common = 0;
            u.forEach(w => { if (a.has(w)) common += 1; });
            const similarity = common / u.size;
            if (similarity > 0.7) {
              console.warn('[V3] Echo blocked:', { similarity, commonWords: common, userWords: u.size });
              toast.warning(language === 'fr' ? 'Écho détecté — attends que le coach finisse' : 'Echo detected');
              hardReset();
              return;
            }
          }
        }
      }

      // Push user bubble + empty assistant bubble (for streaming)
      const now = new Date().toISOString();
      setMessages(prev => [
        ...prev,
        { role: 'user', content: transcription, timestamp: now, type: 'voice', msg_id: userMsgId },
        { role: 'assistant', content: '', timestamp: now, msg_id: assistantMsgId, streaming: true },
      ]);
      // Scroll to bottom so the streaming bubble is always in view
      setTimeout(() => scrollToBottom(true), 50);

      // ─── Step 2: Stream AI response via SSE ───
      const token = localStorage.getItem('session_token');
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/coach-ai/chat-stream`;
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': token || '',
          'Accept': 'text/event-stream',
        },
        body: JSON.stringify({
          message: transcription,
          session_id: sessionIdRef.current,
          msg_id: userMsgId,
        }),
      });
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let fullText = '';

      const appendDelta = (delta) => {
        setMessages(prev => prev.map(m =>
          m.msg_id === assistantMsgId ? { ...m, content: (m.content || '') + delta } : m
        ));
      };

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let frameEnd;
        while ((frameEnd = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, frameEnd);
          buffer = buffer.slice(frameEnd + 2);
          const dataLine = frame.split('\n').find(l => l.startsWith('data:'));
          if (!dataLine) continue;
          let payload;
          try { payload = JSON.parse(dataLine.slice(5).trim()); } catch { continue; }

          if (payload.type === 'session' && payload.session_id) {
            if (!sessionIdRef.current) updateSessionId(payload.session_id);
          } else if (payload.type === 'safety' && payload.resources) {
            setShieldState({ open: true, resources: payload.resources, topics: payload.topics || [] });
          } else if (payload.type === 'chunk' && payload.delta) {
            appendDelta(payload.delta);
            fullText += payload.delta;
          } else if (payload.type === 'done') {
            if (payload.full_text) fullText = payload.full_text;
          } else if (payload.type === 'error') {
            throw new Error(payload.error || 'Stream error');
          }
        }
      }

      // Mark streaming as complete
      setMessages(prev => prev.map(m =>
        m.msg_id === assistantMsgId ? { ...m, content: fullText || m.content, streaming: false } : m
      ));

      // ─── Step 3: (Optional) Generate TTS audio — STRICT serial queue ───
      // Snapshot the current audio generation BEFORE awaiting /voice/speak.
      // If the user triggers another recording/message during the TTS fetch,
      // killAllAudio() bumps the generation and this stale response is dropped
      // on the floor — preventing the "overlapping voices" cacophony.
      if (audioEnabled && fullText) {
        setVoiceState('PLAYING');
        voiceLockRef.current = false;
        const ttsGen = audioGenerationRef.current;
        try {
          const speakResp = await api.post('/voice/speak', { text: fullText });
          // Drop this TTS if a newer voice action has started in the meantime.
          if (audioGenerationRef.current !== ttsGen) {
            return;
          }
          if (speakResp.data?.audio_base64) {
            playAudioResponse(speakResp.data.audio_base64, speakResp.data.format || 'mp3');
          } else {
            hardReset();
          }
        } catch {
          hardReset();
        }
      } else {
        hardReset();
      }
      loadConversations();
    } catch (error) {
      console.error('[V3] Voice stream error:', error);
      toast.error(language === 'fr' ? "Erreur voix — l'équipe YUMIX a été avertie" : 'Voice error — YUMIX team has been notified');
      // Remove empty assistant bubble if stream failed mid-way
      setMessages(prev => prev.filter(m => !(m.msg_id === assistantMsgId && m.streaming && !m.content)));
      hardReset();
      ErrorLogger.log(error, 'Coach_AI_Voice_Stream', {
        status: error?.response?.status,
      });
    }
  };
  
  // ========== V3: PLAY AUDIO — serialized + generation-guarded ==========
  // Guarantees ONE clip at a time. If a second call arrives mid-play, the
  // previous clip is killed cleanly, its onended callback is neutralized via
  // the generation check, and the new clip starts fresh.
  const playAudioResponse = (base64Audio, format = 'mp3') => {
    try {
      // 1. Cut any currently-playing clip (bumps the generation too)
      killAllAudio();
      // 2. Snapshot the new generation — this call "owns" the player
      const myGen = audioGenerationRef.current;
      const audio = new Audio(`data:audio/${format};base64,${base64Audio}`);
      audioPlayerRef.current = audio;
      setVoiceState('PLAYING');
      audio.onended = () => {
        // Ignore stale events from previous clips
        if (audioGenerationRef.current !== myGen) return;
        audioPlayerRef.current = null;
        setLastTranscription(null);
        voiceLockRef.current = false;
        setVoiceState('IDLE');
      };
      audio.onerror = () => {
        if (audioGenerationRef.current !== myGen) return;
        // Friendly UX fallback — usually a Bluetooth headset disconnect, the
        // OS muting the tab, or autoplay policy. Don't surface a console
        // error → just inform the user calmly.
        toast.info(language === 'fr'
          ? 'Impossible de démarrer l\'audio, vérifie tes écouteurs'
          : 'Could not start audio — check your headphones');
        hardReset();
      };
      audio.play().catch(() => {
        if (audioGenerationRef.current !== myGen) return;
        // Same as above for the play() promise rejection (Safari + iOS
        // sometimes reject when the audio device is busy / unplugged).
        toast.info(language === 'fr'
          ? 'Impossible de démarrer l\'audio, vérifie tes écouteurs'
          : 'Could not start audio — check your headphones');
        hardReset();
      });
    } catch (error) { hardReset(); }
  };
  
  const stopAudio = () => { hardReset(); };

  // Format message content with line breaks
  const formatMessage = (content) => {
    return content.split('\n').map((line, i) => (
      <React.Fragment key={i}>
        {line}
        {i < content.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };

  return (
    <Layout>
      {/* Initial Loading State */}
      {initialLoading && (
        <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
          <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center animate-pulse">
            <UserCircle className="w-10 h-10 text-white" />
          </div>
          <div className="text-center">
            <h2 className="font-heading text-xl uppercase mb-2">Assistant YUMIX</h2>
            <p className="text-muted-foreground text-sm flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Chargement en cours...
            </p>
          </div>
        </div>
      )}

      {/* Error State */}
      {loadError && !initialLoading && (
        <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
          <div className="w-16 h-16 bg-red-500/20 rounded-2xl flex items-center justify-center">
            <UserCircle className="w-10 h-10 text-red-500" />
          </div>
          <div className="text-center">
            <h2 className="font-heading text-xl uppercase mb-2 text-red-500">Erreur</h2>
            <p className="text-muted-foreground text-sm mb-4">{loadError}</p>
            <Button 
              onClick={() => window.location.reload()}
              className="bg-orange-500 hover:bg-orange-600"
            >
              Réessayer
            </Button>
          </div>
        </div>
      )}

      {/* Main Content */}
      {!initialLoading && !loadError && (
      <div className="flex flex-col h-[calc(100dvh-140px)] md:max-h-[800px]" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        {/* Header */}
        <div className="flex items-center justify-between gap-2 mb-4 min-w-0">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {sessionId && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setSessionId(null);
                  sessionIdRef.current = null;
                  setMessages([]);
                  localStorage.removeItem('coach_session_id');
                }}
                data-testid="coach-back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
            )}
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <CoachYumixAvatar variant="portrait" size="sm" glowColor="orange" data-testid="coach-header-avatar" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 min-w-0">
                  <h1 className="font-heading text-base sm:text-lg uppercase tracking-tight truncate">Assistant YUMIX</h1>
                  {/* Clinical Mode Badge in Coach Header */}
                  {clinicalMode?.enabled && (
                    <ClinicalModeIndicator clinicalMode={clinicalMode} variant="compact" />
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground truncate">
                  {clinicalMode?.enabled 
                    ? (language === 'fr' ? 'Mode Chef • v5.1' : 'Chef Mode • v5.1')
                    : 'YUMIX • v5.1'}
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowConversations(!showConversations)}
            >
              <MessageSquare className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Historique</span>
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={startNewConversation}
              disabled={loading}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
              <span className="hidden sm:inline">Nouveau</span>
            </Button>
          </div>
        </div>

        {/* Clinical Mode Banner - Shows when clinical mode is active */}
        {clinicalMode?.enabled && (
          <div className="mb-4">
            <ClinicalModeIndicator clinicalMode={clinicalMode} variant="banner" />
          </div>
        )}

        {/* Conversations List */}
        <AnimatePresence>
          {showConversations && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden mb-4"
            >
              <Card className="bg-secondary/50 border-border">
                <CardHeader className="py-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Conversations récentes
                  </CardTitle>
                </CardHeader>
                <CardContent className="py-0 pb-3">
                  {conversations.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Aucune conversation</p>
                  ) : (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {conversations.map((conv) => (
                        <div
                          key={conv.session_id}
                          onClick={() => loadConversation(conv.session_id)}
                          className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                            sessionId === conv.session_id 
                              ? 'bg-orange-500/20 border border-orange-500/50' 
                              : 'bg-background hover:bg-secondary'
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm truncate">{conv.preview || 'Nouvelle conversation'}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(conv.updated_at).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={(e) => deleteConversation(conv.session_id, e)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Chat Area or Welcome Screen — MINDSET 5.0 COMMAND CENTER */}
        {!sessionId && messages.length === 0 && !voiceMode ? (
          <div className="flex-1 flex flex-col space-y-5 px-2 overflow-y-auto pb-4" data-testid="coach-command-center">
            {/* Hero Section */}
            <div className="text-center pt-4">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg shadow-orange-500/20">
                <Zap className="w-9 h-9 text-white" />
              </div>
              <h2 className="font-heading text-xl sm:text-2xl uppercase tracking-tight mb-1" data-testid="coach-hero-title">
                {(() => {
                  const h = new Date().getHours();
                  const name = user?.name?.split(' ')[0] || 'Champion';
                  if (h < 12) return `Bon matin, ${name}`;
                  if (h < 17) return `Bon après-midi, ${name}`;
                  return `Bonne soirée, ${name}`;
                })()}
              </h2>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                Ton Centre de Commande YUMIX. Choisis ton pilier et passe à l'action.
              </p>
            </div>

            {/* 5-Pillar Grid OR Coach Sportif sub-menu (Iter. 406.fn) */}
            <div className="w-full max-w-lg mx-auto" data-testid="pillar-grid">
              {coachSubMenu ? (
                <CoachSportifChips
                  onPick={(question) => {
                    setCoachSubMenu(false);
                    startNewConversation().then(() => {
                      setTimeout(() => sendMessage(question), 500);
                    });
                  }}
                  onBack={() => setCoachSubMenu(false)}
                />
              ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {(() => {
                  const allPillars = [
                  {
                    id: 'meals',
                    icon: UtensilsCrossed,
                    glyph: '🥗',
                    tone: 'emerald',
                    label: 'Analyse Repas',
                    sub: 'Macros & Nutrition',
                    msg: 'Analyse mon dernier repas et dis-moi comment optimiser mes macros pour aujourd\'hui.'
                  },
                  {
                    id: 'chef',
                    icon: ChefHat,
                    glyph: '👨‍🍳',
                    tone: 'amber',
                    label: 'Chef Privé',
                    sub: 'Recettes & Meal Prep',
                    msg: 'Propose-moi une recette rapide, riche en protéines et facile à préparer pour ce soir.'
                  },
                  {
                    id: 'coach',
                    icon: Dumbbell,
                    glyph: '💪',
                    tone: 'blue',
                    label: 'Coach Sportif',
                    sub: 'Training & Recovery',
                    msg: 'Salut ! J\'aimerais en savoir plus sur l\'entraînement. Peux-tu répondre à mes questions sur les techniques, la supplémentation ou la récupération ?'
                  },
                  {
                    id: 'health',
                    icon: Heart,
                    glyph: '❤️',
                    tone: 'rose',
                    label: 'Santé & Jeûne',
                    sub: 'Hydratation & Fasting',
                    msg: 'Conseille-moi sur mon jeûne intermittent, mon hydratation et ma récupération aujourd\'hui.'
                  },
                  {
                    id: 'mindset',
                    icon: Brain,
                    glyph: '🧠',
                    tone: 'violet',
                    label: 'Mindset',
                    sub: 'Mental & Motivation',
                    msg: 'J\'ai besoin de renforcement mental. Aide-moi à recadrer ma perspective et à rester discipliné.'
                  },
                ];
                // Sort by user's personalized order (most-clicked first)
                const sorted = pillarOrder.length > 0
                  ? [...allPillars].sort((a, b) => {
                      const ai = pillarOrder.indexOf(a.id);
                      const bi = pillarOrder.indexOf(b.id);
                      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
                    })
                  : allPillars;
                return sorted;
                })().map((pillar, idx) => (
                  <motion.button
                    key={pillar.id}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => {
                      trackPillarClick(pillar.id);
                      // Iter. 406.fn — "Coach Sportif" opens a topic chooser
                      // BEFORE starting the conversation (techniques /
                      // supplements / recovery / periodization). All other
                      // pillars keep the legacy direct-send behaviour.
                      if (pillar.id === 'coach') {
                        setCoachSubMenu(true);
                        return;
                      }
                      startNewConversation().then(() => {
                        setTimeout(() => sendMessage(pillar.msg), 500);
                      });
                    }}
                    className={`relative p-4 rounded-2xl bg-card border border-border hover:border-orange-500/40 text-left transition-all hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] group ${idx === 4 ? 'col-span-2 sm:col-span-1' : ''}`}
                    data-testid={`pillar-${pillar.id}`}
                  >
                    <PremiumIcon glyph={pillar.glyph} tone={pillar.tone} size="md" withGlow className="mb-3" data-testid={`pillar-icon-${pillar.id}`} />
                    <p className="font-heading text-sm uppercase tracking-wide leading-tight text-foreground">{pillar.label}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{pillar.sub}</p>
                    <ChevronRight className="absolute top-3 right-3 w-4 h-4 text-muted-foreground/40 group-hover:text-orange-400 group-hover:translate-x-0.5 transition-all" />
                  </motion.button>
                ))}
              </div>
              )}
            </div>

            {/* Smart Suggestions — 2-line bubbles */}
            <div className="w-full max-w-lg mx-auto" data-testid="smart-suggestions">
              <p className="text-xs font-medium mb-2 flex items-center gap-1.5 text-muted-foreground uppercase tracking-wider">
                <Sparkles className="w-3.5 h-3.5 text-orange-500" />
                Suggestions rapides
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {suggestedQuestions.slice(0, 4).map((question, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      startNewConversation().then(() => {
                        setTimeout(() => sendMessage(question), 500);
                      });
                    }}
                    className="p-3 rounded-xl text-left text-sm transition-all active:scale-[0.98] flex items-center gap-3 group"
                    data-testid={`suggestion-${i}`}
                    style={{
                      background: 'linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(15,15,15,0.68) 45%, rgba(251,146,60,0.16) 100%)',
                      border: '1px solid rgba(251,146,60,0.4)',
                      boxShadow: '0 0 12px rgba(251,146,60,0.3), inset 0 1px 0 rgba(255,255,255,0.05)',
                      backdropFilter: 'blur(6px)',
                      color: '#fed7aa',
                    }}
                  >
                    <FlameKindling
                      className="w-4 h-4 flex-shrink-0"
                      style={{
                        color: '#fed7aa',
                        filter: 'drop-shadow(0 0 5px rgba(251,146,60,0.85))',
                      }}
                    />
                    <span className="line-clamp-2 leading-snug" style={{ textShadow: '0 0 4px rgba(251,146,60,0.4)' }}>
                      {question}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="text-center pt-1">
              <Button
                onClick={startNewConversation}
                disabled={loading}
                className="bg-orange-500 hover:bg-orange-600 shadow-lg shadow-orange-500/20"
                size="lg"
                data-testid="start-conversation-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Zap className="w-5 h-5 mr-2" />}
                Conversation libre
              </Button>
            </div>
          </div>
        ) : (
          // Chat Interface
          <div className="flex flex-col flex-1 min-h-0">
            <Card className="flex-1 flex flex-col bg-card/50 border-border overflow-hidden min-h-0">
              <ScrollArea
                ref={scrollContainerRef}
                className="flex-1 p-4"
                style={{ overscrollBehavior: 'contain' }}
                onScrollCapture={(e) => {
                  const viewport = e.currentTarget.querySelector('[data-radix-scroll-area-viewport]');
                  if (!viewport) return;
                  const { scrollTop, scrollHeight, clientHeight } = viewport;
                  userScrolledUpRef.current = scrollHeight - scrollTop - clientHeight > 120;
                }}
              >
                <div className="space-y-4">
                  {messages.map((msg, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex gap-3 w-full min-w-0 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                    >
                      {msg.role === 'user' ? (
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden relative bg-primary"
                          style={{ boxShadow: '0 0 12px rgba(168,85,247,0.35), inset 0 0 8px rgba(216,180,254,0.18)' }}
                        >
                          <User className="w-4 h-4 text-primary-foreground" />
                        </div>
                      ) : (
                        <CoachYumixAvatar
                          variant="portrait"
                          size="sm"
                          glowColor="orange"
                          animate={!!msg.streaming}
                          className="!rounded-xl"
                          data-testid="coach-message-avatar"
                        />
                      )}
                      <div
                        className={`max-w-[80%] min-w-0 p-3 rounded-xl break-words overflow-hidden ${
                          msg.role === 'user'
                            ? 'text-primary-foreground'
                            : 'text-foreground'
                        }`}
                        style={
                          msg.role === 'user'
                            ? {
                                background:
                                  'linear-gradient(135deg, rgba(168,85,247,0.85), rgba(107,33,168,0.85))',
                                border: '1px solid rgba(192,132,252,0.40)',
                                boxShadow:
                                  '0 0 18px rgba(168,85,247,0.30), inset 0 0 0 1px rgba(216,180,254,0.10)',
                              }
                            : {
                                background:
                                  'linear-gradient(135deg, rgba(28,12,5,0.85), rgba(15,3,30,0.95))',
                                border: '1px solid rgba(251,146,60,0.30)',
                                boxShadow:
                                  '0 0 16px rgba(251,146,60,0.18), inset 0 0 0 1px rgba(251,146,60,0.08)',
                                backdropFilter: 'blur(6px)',
                              }
                        }
                      >
                        {msg.type === 'voice' && msg.role === 'user' && (
                          <div className="flex items-center gap-1 mb-1 opacity-70">
                            <Mic className="w-3 h-3" />
                            <span className="text-[10px] uppercase tracking-wider font-medium">
                              {language === 'fr' ? 'Message vocal' : 'Voice message'}
                            </span>
                          </div>
                        )}
                        {msg.role === 'assistant' && msg.streaming && !msg.content && (
                          <div className="flex items-center gap-1.5 py-1.5" data-testid="coach-streaming-indicator" aria-label="L'assistant réfléchit">
                            <span className="w-2 h-2 rounded-full bg-primary/80 animate-yumix-pulse" style={{ animationDelay: '0ms' }} />
                            <span className="w-2 h-2 rounded-full bg-primary/80 animate-yumix-pulse" style={{ animationDelay: '160ms' }} />
                            <span className="w-2 h-2 rounded-full bg-primary/80 animate-yumix-pulse" style={{ animationDelay: '320ms' }} />
                          </div>
                        )}
                        <CoachMessageContent content={msg.content} />
                        {/* PLAN B: Save as Protocol button — VIP only, detect plan-like AI messages */}
                        {msg.role === 'assistant' && user?.is_beta_tester && /(\d+\s*(?:séries|series|sets)|\d+\s*(?:rép|rep))/i.test(msg.content) && (
                          <button
                            onClick={async () => {
                              try {
                                await api.post('/training-plans/save-protocol', {
                                  plan_name: language === 'fr' ? 'Protocole de readaptation' : 'Rehab Protocol',
                                  plan_content: msg.content,
                                  source: 'ai_chat',
                                });
                                toast.success(language === 'fr' ? 'Protocole sauvegarde (Plan B) !' : 'Protocol saved (Plan B)!');
                              } catch {
                                toast.error(language === 'fr' ? 'Erreur de sauvegarde' : 'Save error');
                              }
                            }}
                            className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-pink-500/10 border border-pink-500/30 hover:bg-pink-500/20 transition-colors"
                            data-testid="save-protocol-btn"
                          >
                            <Download className="w-4 h-4 text-pink-400" />
                            <span className="text-xs text-pink-400 font-bold uppercase tracking-wide">
                              {language === 'fr' ? 'Sauvegarder comme Protocole (Plan B)' : 'Save as Protocol (Plan B)'}
                            </span>
                          </button>
                        )}
                        {/* Copilot action badges */}
                        {msg.actions && msg.actions.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {msg.actions.filter(a => a.success).map((action, ai) => (
                              action.tool === 'navigate' ? (
                                <button
                                  key={ai}
                                  onClick={() => navigate(action.route)}
                                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/10 border border-primary/30 hover:bg-primary/20 transition-colors w-full text-left cursor-pointer"
                                  data-testid={`action-badge-${action.tool}`}
                                >
                                  <Navigation className="w-3.5 h-3.5 text-primary" />
                                  <span className="text-[11px] text-primary font-bold uppercase tracking-wide flex-1">
                                    {action.message || (language === 'fr' ? 'Aller a la section' : 'Go to section')}
                                  </span>
                                  <ChevronRight className="w-3.5 h-3.5 text-primary" />
                                </button>
                              ) : (
                                <div key={ai} className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-green-500/10 border border-green-500/20" data-testid={`action-badge-${action.tool}`}>
                                  {action.tool === 'update_weight' && <Weight className="w-3.5 h-3.5 text-green-400" />}
                                  {action.tool === 'log_meal' && <UtensilsCrossed className="w-3.5 h-3.5 text-green-400" />}
                                  {action.tool === 'log_water' && <Droplets className="w-3.5 h-3.5 text-green-400" />}
                                  {action.tool === 'create_grocery_list' && <ShoppingCart className="w-3.5 h-3.5 text-green-400" />}
                                  <span className="text-[11px] text-green-400 font-medium">{action.message}</span>
                                  <CheckCircle2 className="w-3 h-3 text-green-500 ml-auto" />
                                </div>
                              )
                            ))}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                  
                  {sending && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex gap-3"
                    >
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                        <UserCircle className="w-4 h-4 text-white" />
                      </div>
                      <div className="bg-secondary p-3 rounded-xl">
                        <div className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                          <span className="text-sm text-muted-foreground">Coach réfléchit...</span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                  
                  {/* Scroll anchor — no scrollIntoView, handled by scrollContainerRef */}
                  <div ref={messagesEndRef} style={{ height: 1 }} />
                </div>
              </ScrollArea>
            </Card>

            {/* Input Area - Sticky at bottom */}
            <div className="flex-shrink-0 mt-4 space-y-3 pb-safe" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 12px)' }}>
              {/* Voice/Text Mode Toggle */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button
                    variant={voiceMode ? "default" : "outline"}
                    size="sm"
                    onClick={() => voiceMode ? setVoiceMode(false) : enableVoiceMode()}
                    className={voiceMode ? "bg-orange-500 hover:bg-orange-600" : ""}
                  >
                    <Mic className="w-4 h-4 mr-1" />
                    {language === 'fr' ? 'Mode Voix' : 'Voice Mode'}
                  </Button>
                  
                  {voiceMode && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setAudioEnabled(!audioEnabled)}
                      className="text-muted-foreground"
                    >
                      {audioEnabled ? (
                        <Volume2 className="w-4 h-4" />
                      ) : (
                        <VolumeX className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                </div>
                
                {processingVoice && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    {language === 'fr' ? 'Traitement...' : 'Processing...'}
                  </span>
                )}
              </div>
              
              
              {/* Voice Input */}
              {voiceMode ? (
                <div className="flex flex-col items-center gap-4 py-6">
                  {/* Indicator when Coach is speaking */}
                  {isAudioPlaying && (
                    <div className="flex items-center gap-2 text-orange-400 animate-pulse mb-2">
                      <Volume2 className="w-5 h-5" />
                      <span className="text-sm font-medium">
                        {language === 'fr' ? 'YUMIX parle...' : 'YUMIX is speaking...'}
                      </span>
                    </div>
                  )}
                  <motion.button
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={processingVoice || sending || isAudioPlaying}
                    className={`w-20 h-20 rounded-full flex items-center justify-center transition-all ${
                      isRecording 
                        ? 'bg-red-500 animate-pulse shadow-lg shadow-red-500/50'
                        : isAudioPlaying
                          ? 'bg-gray-500 cursor-not-allowed opacity-60'
                          : 'bg-orange-500 hover:bg-orange-600 hover:scale-105'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    whileTap={{ scale: 0.95 }}
                    data-testid="voice-record-btn"
                  >
                    {processingVoice ? (
                      <Loader2 className="w-10 h-10 text-white animate-spin" />
                    ) : isRecording ? (
                      <MicOff className="w-10 h-10 text-white" />
                    ) : (
                      <Mic className="w-10 h-10 text-white" />
                    )}
                  </motion.button>
                  
                  <p className="text-sm text-muted-foreground text-center">
                    {isRecording 
                      ? (language === 'fr' ? '🔴 Enregistrement... Cliquez pour arrêter' : '🔴 Recording... Click to stop')
                      : processingVoice
                        ? (language === 'fr' ? 'YUMIX ecoute...' : 'YUMIX is listening...')
                        : isAudioPlaying
                          ? (language === 'fr' ? 'Attendez que YUMIX finisse de parler...' : 'Wait for YUMIX to finish speaking...')
                          : (language === 'fr' ? 'Appuyez pour parler a YUMIX' : 'Press to talk to YUMIX')
                    }
                  </p>
                  
                  {/* Transcription indicator - shows what Whisper understood */}
                  {lastTranscription && !isRecording && !processingVoice && (
                    <motion.div
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="w-full max-w-sm mx-auto mt-2"
                      data-testid="transcription-indicator"
                    >
                      <div className="bg-primary/10 border border-primary/20 rounded-lg px-3 py-2 text-center">
                        <div className="flex items-center justify-center gap-1.5 mb-0.5">
                          <Mic className="w-3 h-3 text-primary" />
                          <span className="text-[10px] uppercase tracking-wider font-semibold text-primary">
                            {language === 'fr' ? 'Transcription Whisper' : 'Whisper Transcription'}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground italic leading-relaxed">
                          "{lastTranscription.length > 100 ? lastTranscription.substring(0, 100) + '...' : lastTranscription}"
                        </p>
                      </div>
                    </motion.div>
                  )}
                  
                  <p className="text-xs text-muted-foreground">
                    {language === 'fr' 
                      ? 'Trop de bruit? Passez au mode texte ↑' 
                      : 'Too noisy? Switch to text mode ↑'}
                  </p>
                </div>
              ) : (
                /* Text Input */
                <div className="flex gap-2">
                  <Input
                    ref={inputRef}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={language === 'fr' ? "Pose ta question a l'assistant..." : "Ask the assistant a question..."}
                    disabled={sending}
                    className="flex-1 bg-secondary border-border"
                    data-testid="coach-ai-input"
                  />
                  <Button
                    onClick={() => sendMessage()}
                    disabled={sending || !message.trim()}
                    className="bg-orange-500 hover:bg-orange-600"
                    data-testid="coach-ai-send"
                  >
                    {sending ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </Button>
                </div>
              )}
            </div>

            {/* Quick Questions — GLOW aesthetic (Iter. 411.aa) */}
            {messages.length <= 2 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {suggestedQuestions.slice(0, 3).map((q, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(q)}
                    disabled={sending}
                    className="text-[11px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full transition-all active:scale-95 disabled:opacity-50"
                    style={{
                      background: 'linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(15,15,15,0.68) 45%, rgba(251,146,60,0.18) 100%)',
                      border: '1px solid rgba(251,146,60,0.45)',
                      boxShadow: '0 0 10px rgba(251,146,60,0.35), inset 0 1px 0 rgba(255,255,255,0.05)',
                      backdropFilter: 'blur(6px)',
                      color: '#fed7aa',
                      textShadow: '0 0 4px rgba(251,146,60,0.55)',
                    }}
                  >
                    {q.length > 40 ? q.substring(0, 40) + '...' : q}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* 🛡️ ZERO RELAIS — Legal Shield Modal (triggered by SSE safety event) */}
      <LegalShieldModal
        open={shieldState.open}
        resources={shieldState.resources}
        topics={shieldState.topics}
        language={(user?.language || 'fr').toLowerCase().slice(0, 2)}
        authToken={localStorage.getItem('access_token')}
        trigger="ai_gate"
        onClose={() => setShieldState((s) => ({ ...s, open: false }))}
        onAcknowledge={() => {}}
      />
    </Layout>
  );
};

export default CoachAI;

/**
 * Iter. 406.fn — Coach Sportif topic chooser.
 * Rendered IN PLACE of the 5-pillar grid when the user picks the blue
 * "Coach Sportif" card. Each chip fires sendMessage(question) immediately
 * via the parent's `onPick`. Back button restores the full pillar grid.
 */
function CoachSportifChips({ onPick, onBack }) {
  const TOPICS = [
    {
      emoji: '🏋️',
      title: 'Technique d\u2019exécution',
      sub: 'Squat, deadlift, bench…',
      q: 'Explique-moi la technique parfaite du squat, du deadlift et du bench press : les erreurs fréquentes à éviter et les cues mentaux pour bien exécuter.',
      testid: 'coach-chip-technique',
    },
    {
      emoji: '💊',
      title: 'Supplémentation sportive',
      sub: 'Créatine, protéine, BCAA…',
      q: 'Quels suppléments ont une vraie base scientifique pour la performance et l\u2019hypertrophie ? Dis-moi quoi prendre, à quelle dose, et quand.',      testid: 'coach-chip-supplements',
    },
    {
      emoji: '😴',
      title: 'Récupération optimale',
      sub: 'Sommeil, mobilité, deload',
      q: 'Comment optimiser ma récupération entre les séances ? Sommeil, mobilité, deload weeks, gestion du stress — donne-moi un protocole concret.',
      testid: 'coach-chip-recovery',
    },
    {
      emoji: '📊',
      title: 'Périodisation & progression',
      sub: 'Volume, intensité, deload',
      q: 'Explique-moi la périodisation : comment varier volume et intensité sur 4-6-12 semaines pour progresser sans plateau ni blessure.',
      testid: 'coach-chip-periodization',
    },
  ];
  return (
    <div className="space-y-3" data-testid="coach-sportif-chips">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          className="text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground inline-flex items-center gap-1 transition-colors"
          data-testid="coach-chip-back"
        >
          <span aria-hidden>←</span> Retour
        </button>
        <h3 className="text-sm font-heading uppercase tracking-tight text-blue-300 ml-auto">
          Sur quoi tu veux apprendre ?
        </h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {TOPICS.map((t) => (
          <button
            key={t.testid}
            type="button"
            onClick={() => onPick(t.q)}
            className="text-left p-3 rounded-xl bg-gradient-to-br from-blue-500/15 to-blue-700/10 hover:from-blue-500/25 hover:to-blue-700/20 border border-blue-500/30 hover:border-blue-400/50 transition-all"
            data-testid={t.testid}
          >
            <div className="flex items-start gap-2.5">
              <span className="text-2xl shrink-0 leading-none">{t.emoji}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-foreground leading-tight">{t.title}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{t.sub}</p>
              </div>
            </div>
          </button>
        ))}
      </div>
      <p className="text-[10px] text-muted-foreground/70 text-center pt-1">
        💡 Pour générer ton plan d&apos;entraînement complet, va dans l&apos;onglet Entraînement → Créer mon plan (IA).
      </p>
    </div>
  );
}
